import { computed, defineComponent } from "vue";
import { usePortalStore } from "@/framework/portal";

export default defineComponent ({
    name: "PhotoPage",
    inheritAttrs: false,
    props: {
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: false
        }
    },
    setup() {
        const portal = usePortalStore();        
        return {
            portal
        };
    },
    mounted() {
        this.canvas = this.$refs.canvas;
        this.video = this.$refs.video;

        this.portal.disableNext = true;
        this.validationHidden = !this.portal.photoValidationEnabled;
        this.targetWidth = this.portal.photoDetails.width;
        this.targetHeight = this.portal.photoDetails.height;
        this.startCapture();
        this.initCanvas();
    },
    beforeUnmount() {
        try {
            const stream = this.video.srcObject;
            const tracks = stream.getTracks();          
            tracks.forEach((track: any) => {
              track.stop();
            });          
            this.video.srcObject = null; 
        } catch (error) {
            console.log('stop video error', error);
        }
    },
    data() {
        return {
            video: null as any,
            canvas: null as any,
            targetWidth: 160 as number,
            targetHeight: 240 as number,
            photoStyle: null as any,
            videoReady: false as Boolean,
            showPhoto: false as Boolean,
            videoHidden: false as Boolean,
            validationPassed: false as Boolean,
            validationCompleted: false as Boolean,
            validationHidden: false as Boolean,
            validationError: false as Boolean,
            validating: false as Boolean,
            faceNotRotated: false as Boolean,
            faceCloseToFrame: false as Boolean,
            faceCentreAligned: false as Boolean,
            whiteBackground: false as Boolean
        }
    },
    methods: {
        startCapture() {
            navigator.mediaDevices.getUserMedia({ video: true, audio: false })
            .then(stream => {
                this.video.srcObject = stream;
                this.video.play();
                this.videoReady = true;
            })
            .catch(error => {
                // TODO - fix later, TEMP - there is currently a spinner where the camera playback is
                console.log(error);
            })
        },
        initCanvas() {
            // make sure css style on video and image elements has correct aspect ratio
            this.photoStyle = computed(() => ({
                aspectRatio: `${this.targetWidth} / ${this.targetHeight}`,
            }));
   
            this.canvas.setAttribute('width', this.targetWidth);
            this.canvas.setAttribute('height', this.targetHeight);

            this.canvas.style.width = this.targetWidth + 'px';
            this.canvas.style.height = this.targetHeight + 'px';    
        },
        takePicture() {
            const context = this.canvas.getContext('2d');

            const targetAspect = this.targetWidth / this.targetHeight;

            const videoWidth = this.video.videoWidth;
            const videoHeight = this.video.videoHeight;
            const videoAspect = videoWidth / videoHeight;

            let sx = 0, sy = 0, sw = videoWidth, sh = videoHeight;

            if (videoAspect > targetAspect) {
                // Video is wider — crop left/right
                sw = videoHeight * targetAspect;
                sx = (videoWidth - sw) / 2;
            } else {
                // Video is taller — crop top/bottom
                sh = videoWidth / targetAspect;
                sy = (videoHeight - sh) / 2;
            }

            context.drawImage(
                this.video,
                sx, sy, sw, sh,   // source rectangle (crop)
                0, 0, this.targetWidth, this.targetHeight  // destination canvas
            );

            this.portal.photoData = this.canvas.toDataURL('image/png');
            this.showPhoto = true;
            this.videoHidden = true;
            if (this.portal.photoValidationEnabled)
                this.validate();
            else {
                this.validationCompleted = true;
                this.portal.disableNext = false;
            }
        },
        resetPhoto() {
            this.portal.photoData = undefined;
            this.showPhoto = false;
            this.validationCompleted = false;
            this.validationError = false;
            this.videoHidden = false;
            this.portal.disableNext = true;
            this.startCapture();
            this.initCanvas() 
        },
        handleValidateError() {
            // TODO - will be fixed/handled better when smart face config is updated
            //if (error) console.log("Error:", error);
            this.portal._quasar.loading.hide();
            this.validating = false;
            this.validationCompleted = true;
            this.validationError = true;
        },
        validate() {
            this.portal._quasar.loading.show();
            this.validating = true;
            this.validationCompleted = false;
            fetch(`${this.portal.getApiPath()}externalPortal/ValidatePhoto`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(this.portal.photoData.replace("data:image/png;base64,", ""),),
            })
            .then((response) => {
                if(response.ok) {
                    return response.json();
                }            
                this.handleValidateError();             
            })
            .then((result) => {
                this.portal._quasar.loading.hide();
                this.validationCompleted = true;
                this.validationPassed = !result.error && result.validation_passed;
                this.faceNotRotated = result.features_validation_report.image_has_face_not_rotated;
                this.faceCloseToFrame = result.features_validation_report.image_has_face_close_to_frame;
                this.faceCentreAligned = result.features_validation_report.image_has_face_centre_aligned;
                this.whiteBackground = result.features_validation_report.image_has_white_background;

                if (this.validationPassed)
                {
                    this.portal.photoData = result.output_image;   
                }

                this.validating = false;
                this.portal.disableNext = !this.validationPassed;
            })
            .catch((error) => {
                this.handleValidateError(); 
            });
        }
    }
});